import React, { useState } from 'react';
import { X, ShoppingCart, AlertCircle, CheckCircle2 } from 'lucide-react';
import { participateInDeal, BUSINESS } from '../config';
import { useAuth } from '../context/AuthContext';

export default function DealParticipationModal({ deal, onClose, onSuccess }) {
  const { user } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const depositAmount = (() => {
    const price = deal.current_price || deal.base_price;
    let amount;
    if (deal.deposit_type === 'percentage') {
      amount = price * quantity * deal.deposit_value / 100;
    } else {
      amount = deal.deposit_value * quantity;
    }
    const min = (deal.deposit_min || 0) * quantity;
    return Math.max(amount, min);
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await participateInDeal(deal.deal_id, {
        ...form,
        quantity,
      });
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message || 'Erreur lors de la participation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-sm border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-sm hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        <div className="p-6">
          {success ? (
            /* Success screen */
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white font-heading mb-2">
                Participation confirmee !
              </h3>
              <p className="text-slate-600 mb-6">
                Vous avez rejoint le deal. Vous serez contacte lorsque le deal sera valide.
              </p>
              <div className="bg-slate-100 rounded-sm p-4 text-left space-y-2">
                <p className="text-sm text-slate-700">
                  <span className="text-slate-500">Retrait :</span> {BUSINESS.adresse}
                </p>
                <p className="text-sm text-slate-700">
                  <span className="text-slate-500">Tel :</span> {BUSINESS.telephone}
                </p>
              </div>
              <button
                onClick={onClose}
                className="mt-6 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-8 py-2.5 hover:bg-secondary transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            /* Form */
            <>
              <h3 className="text-xl font-bold text-white font-heading mb-1">
                Rejoindre ce deal
              </h3>
              <p className="text-sm text-slate-600 mb-6">{deal.title}</p>

              {error && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-sm p-3 mb-4">
                  <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-rose-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quantite</label>
                  <select
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="w-full bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-white focus:ring-secondary focus:border-secondary"
                  >
                    {[...Array(10)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>

                {/* Contact fields (hidden if user is logged in with complete profile) */}
                {!user && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Prenom</label>
                        <input
                          required
                          value={form.first_name}
                          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                          className="w-full bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-white focus:ring-secondary focus:border-secondary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                        <input
                          required
                          value={form.last_name}
                          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                          className="w-full bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-white focus:ring-secondary focus:border-secondary"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-white focus:ring-secondary focus:border-secondary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Telephone</label>
                      <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-white focus:ring-secondary focus:border-secondary"
                      />
                    </div>
                  </>
                )}

                {/* Deposit summary */}
                <div className="bg-secondary/5 border border-secondary/15 rounded-sm p-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Prix unitaire</span>
                    <span className="text-white">{(deal.current_price || deal.base_price).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Quantite</span>
                    <span className="text-white">x{quantity}</span>
                  </div>
                  <div className="border-t border-slate-200 my-2" />
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-secondary">Acompte a regler</span>
                    <span className="text-secondary">{depositAmount.toFixed(2)} €</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Solde a regler en magasin au retrait.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3 hover:bg-secondary transition-colors shadow-md disabled:opacity-50"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {loading ? 'Inscription...' : 'Rejoindre le deal'}
                </button>
              </form>

              <p className="text-[10px] text-slate-500 text-center mt-3">
                En participant, vous acceptez nos conditions de vente.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
